import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/styles';
import { differenceInMinutes, format, parseISO } from 'date-fns';
import React from 'react';

import { getFullName } from 'src/common/formatting/getFullName';
import { Colors } from 'src/common/ui/base/Colors';
import { AcceptNextPatientScheduleItemType } from 'src/myDayToday/types/acceptNextPatientCard';
import type { AcceptNextPatientScheduleItem } from 'src/myDayToday/types/acceptNextPatientCard';

type FyiBannerProps = {
  scheduleItem: AcceptNextPatientScheduleItem;
  now: Date;
};

function formatRelativeTime(target: Date, now: Date): string {
  const mins = differenceInMinutes(target, now);
  if (mins >= 0) {
    return `${format(target, 'h:mm a')} (in ${mins} min)`;
  }
  const elapsed = differenceInMinutes(now, target);
  return `Start: ${format(target, 'h:mm a')} (Elapsed: ${elapsed} min)`;
}

export function FyiBanner({ scheduleItem, now }: FyiBannerProps): JSX.Element {
  const styles = useFyiBannerStyles();
  const itemStart = parseISO(scheduleItem.start);
  const isInProgress = itemStart < now;

  const timingNote = (() => {
    // No trailing space: the label is its own flex item and bannerLeft's `gap`
    // provides the spacing to the description (matching the icon↔label gap).
    if (scheduleItem.type === AcceptNextPatientScheduleItemType.ScheduledVisit && isInProgress) {
      return 'In progress:';
    }
    return 'Coming up:';
  })();

  const itemDescription = (() => {
    if (scheduleItem.type === AcceptNextPatientScheduleItemType.ScheduledVisit) {
      const { patient } = scheduleItem;
      const name = getFullName(patient) || 'Scheduled visit';
      return `Scheduled visit with ${name}`;
    }
    if (scheduleItem.type === AcceptNextPatientScheduleItemType.ScheduledBreak) {
      return 'Scheduled break';
    }
    return 'End of your availability';
  })();

  const timeLabel = formatRelativeTime(itemStart, now);

  return (
    <div className={isInProgress ? styles.bannerCoral : styles.bannerBlue}>
      <div className={styles.bannerLeft}>
        <InfoOutlinedIcon
          className={styles.infoIcon}
          style={{ color: isInProgress ? Colors.Coral2 : Colors.Ocean, width: 24, height: 24 }}
        />
        <span className={styles.timingLabel}>{timingNote}</span>
        <span className={styles.itemDescription}>{itemDescription}</span>
      </div>
      <span className={styles.bannerTime}>{timeLabel}</span>
    </div>
  );
}

const useFyiBannerStyles = makeStyles({
  bannerBlue: {
    background: Colors.Ice,
    borderLeft: `4px solid ${Colors.Ocean}`,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  bannerCoral: {
    background: Colors.Coral1,
    borderLeft: `4px solid ${Colors.Coral2}`,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  bannerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    // minWidth:0 lets this flex item shrink below its content size so the
    // description child can truncate with an ellipsis instead of overflowing.
    minWidth: 0,
  },
  infoIcon: {
    flexShrink: 0,
  },
  timingLabel: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    fontWeight: 600,
    color: Colors.CoolGray9,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  itemDescription: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    fontWeight: 400,
    color: Colors.CoolGray9,
    // minWidth:0 on the truncating element is the second half of the flex
    // ellipsis pattern — without it overflow/textOverflow have no effect in a row.
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bannerTime: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    fontWeight: 600,
    color: Colors.CoolGray9,
    whiteSpace: 'nowrap',
    marginLeft: 8,
  },
});
